// src/pages/billing/BillingListPage.tsx
// Chart-of-Accounts list. "+ New account" opens a create dialog in place
// (AccountFormDialog) instead of navigating to a /new route — see
// docs/ux/14-forms.md. Row click opens the account's inline-editable detail
// page (src/pages/billing/BillingDetailPage.tsx); there is no /edit route.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useBilling, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { AccountFormDialog } from '@/features/billing/AccountFormDialog';
import type { Account } from '@core/types/billing';

/**
 * Chart-of-accounts list page.
 */
export const BillingListPage = observer(() => {
  const store = useBilling();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    store.ListAccounts({
      pagination: {
        cursor: searchParams.get('cursor') || '',
        limit: 20,
        direction: 'forward',
      },
      sort: [],
      date_range: {} as any,
      filters: {} as any,
      view: 0,
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  const handleCreated = (account: Account) => {
    setCreateOpen(false);
    if (account.entity?.id) {
      navigate(`/billing/chart-of-accounts/${account.entity.id}`);
    } else {
      load();
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="sr-only">{t('billing.chartOfAccounts.title')}</h1>
        <Button className="sm:ml-auto" onClick={() => setCreateOpen(true)}>{t('billing.chartOfAccounts.create')}</Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth={false}
              className="flex-1"
            />
            <Button type="submit" variant="secondary">
              {t('common.search')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('billing.chartOfAccounts.code')}</TableHead>
              <TableHead>{t('common.name')}</TableHead>
              <TableHead>{t('common.description')}</TableHead>
              <TableHead>{t('billing.chartOfAccounts.isSystem')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={5} message={t('billing.chartOfAccounts.noItems')} />
            ) : (
              store.items.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    <Link
                      to={`/billing/chart-of-accounts/${item.entity?.id || item.id}`}
                      className="font-mono text-xs text-primary hover:underline"
                    >
                      {String(item.code ?? '')}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/billing/chart-of-accounts/${item.entity?.id || item.id}`} className="hover:underline">
                      {String(item.name ?? '')}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {String(item.description ?? '')}
                  </TableCell>
                  <TableCell>
                    {item.is_system ? t('common.yes') : t('common.no')}
                  </TableCell>
                  <TableCell>
                    {item.is_active ? t('common.active') : t('common.inactive')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <AccountFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        onError={setError}
      />
    </div>
  );
});

export default BillingListPage;
